// The retail partner this build represents. Sent as the `subpath` header on
// every API request so the backend scopes data to the right tenant.
//
// Invariant: no other file should hardcode a tenant string. After cherry-picking
// from upstream (ring-builder), run:
//
//   grep -rni "helzberg" src
//
// and expect hits only on `helzberg_key` / `helzberg_custom_sku`, which are
// backend API field names rather than tenant references. Upstream adds endpoints
// to customizerAPI.ts regularly and they arrive with 'helzberg' baked in.
//
// The grep is case-INSENSITIVE on purpose: upstream also ships PascalCase
// Salesforce Commerce site IDs like `Sites-Helzberg-Site`, which a case-sensitive
// grep silently misses. That exact miss is how an old Helzberg PDP path once
// survived the first de-Helzberg pass.
export const TENANT = 'ben-bridge';

// URL path prefix the builder is served under: /<PATH_PREFIX>/rings/...
// Currently identical to TENANT, but kept as a separate constant because these
// are different concerns — TENANT is an HTTP header value, this is a route
// segment. APP_STYLES in src/utils/basic.ts is keyed by this value.
export const PATH_PREFIX = TENANT;

// Storefront product-detail route the builder hands off to when a customer taps
// the primary CTA (see DONE_BUTTON_LABEL below and src/pages/index.tsx).
//
// Ben Bridge PDP (relative to the storefront host):
//   /999999.html?lbuuid={uuid}&pid={ring_category_sku}
// e.g. https://benbridge.com/999999.html?lbuuid=xxxx-xxxx-xxxx&pid=ABC123
//
// The .html product id is always static (999999). Helzberg-style pid/lbuuid query
// params remain; pid carries the real ring_category_sku. The storefront GET on
// page load uses lbuuid to load the custom product.
export const STOREFRONT_PDP_PATH = '/999999.html';

// Primary CTA label (previously "Done"). Shared because it renders from three
// places — the desktop sidebar footer and both mobile bars in src/pages/index.tsx.
export const DONE_BUTTON_LABEL = 'Finish the Design';

// Opened directly on our production host a relative path lands on our host's 404, so
// hand off to the storefront origin explicitly. Staging keeps the relative path, since
// its saved rings don't exist on the production storefront.
const PRODUCTION_BUILDER_HOST = 'ben-bridge.lashbrookdesigns.com';

export const buildStorefrontPdpUrl = (sku: string, lbuuid: string): string => {
  const origin = window.location.hostname === PRODUCTION_BUILDER_HOST ? STOREFRONT_ORIGIN : '';
  return `${origin}${STOREFRONT_PDP_PATH}?lbuuid=${encodeURIComponent(lbuuid)}&pid=${encodeURIComponent(sku)}`;
};

// Saved as custom_sku when the builder URL has no product_id (e.g. a ring's QR code),
// so the storefront's lbuuid redirect has an SFCC product to land on. Ben Bridge chose
// this existing product for now; swap in a dedicated one if they add it.
export const DEFAULT_STOREFRONT_PRODUCT_ID = '0001600';

// Where the summary QR sends a scan. Production wraps the ring in Ben Bridge's SFCC
// ProductBuilder page; staging has no storefront, so it points at the builder directly.
export const STOREFRONT_ORIGIN = 'https://www.benbridge.com';
export const STOREFRONT_BUILDER_PATH = '/on/demandware.store/Sites-BenBridge-Site/en_US/Product-ProductBuilder';
export const STAGING_BUILDER_ORIGIN = 'https://ben-bridge.staging.lashbrookdesigns.com';

// The storefront expects product_id and base_ring_url as their own params beside
// ring_url, not nested inside it, e.g.
//   ...Product-ProductBuilder?ring_url=<path?ring options>&product_id=...&base_ring_url=...
export const buildStorefrontBuilderUrl = (pathname: string, search: string): string => {
  const ringParams = new URLSearchParams(search);
  const outer = new URLSearchParams();
  ['product_id', 'base_ring_url'].forEach((key) => {
    const value = ringParams.get(key);
    if (value) {
      outer.set(key, value);
    }
    ringParams.delete(key);
  });
  const ringQuery = ringParams.toString();
  const ringUrl = `${pathname}${ringQuery ? `?${ringQuery}` : ''}`;
  const rest = outer.toString();
  return `${STOREFRONT_ORIGIN}${STOREFRONT_BUILDER_PATH}?ring_url=${encodeURIComponent(ringUrl)}${rest ? `&${rest}` : ''}`;
};
